import {
  FormMachineContext,
  FormEvent,
  FormInterpreter,
  FormState,
  FormContext,
  formMachine,
  useFields,
  useFormService,
  FieldPropMap,
  IFormContext,
  PropMap,
  FieldHandler,
  useField,
  ComponentRenderer,
  ComponentDescriptor,
  useFormInterpreter,
  useCurrentStepIndex,
  useForm,
} from '@kizi/comparator-core';
import { Button } from '@kizi/core-components';
import { useInterpret } from '@xstate/react';
import React, { useMemo, useEffect, useContext } from 'react';
import { get, set, merge } from 'lodash';
import { scrollToTop } from '../utils/global';

export type Optional<O extends object, K extends keyof O> = Partial<
  Pick<O, K>
> &
  Omit<O, K>;

type FormListener = (
  state: FormState,
  e: FormEvent,
  form: FormInterpreter
) => boolean | void;

export type FormProps = Optional<
  FormMachineContext,
  'name' | 'validators' | 'transformers'
> &
  Optional<IFormContext, 'defaultPropMap'> & {
    onSubmit?: FormListener;
    onSubmitted?: FormListener;
    onBlur?: FormListener;
    onChange?: FormListener;
    keepAlive?: boolean;
    interpreterCallback?: (service: FormInterpreter) => void;
    onRender?: (i: number) => void;
    formIndex?: number;
  };

/**
 * @example
 * <FormInput name="firstname" component={<BasicInput />} />;
 * <FormInput name="firstname" component={<BasicInput />} container={<Div />} />;
 */
export type FormInputProps<T extends React.ReactElement = React.ReactElement> =
  {
    name: string;
  } & Pick<FormComponentProps<T>, 'component' | 'container'> &
    Optional<FormComponentProps<T>, 'propMap'>;

/**
 * @example
 * <FormComponent component={<Address />} container={<Div />} houseNumber={{'deeper.nested.prop._error': 'housenumber.errors', value: 'housenumber.value'}} />;
 */
export type FormComponentProps<
  T extends React.ReactElement = React.ReactElement
> = {
  component: T;
  container?: React.ReactElement<React.PropsWithChildren<any>>;
  propMap: FieldPropMap;
};

export interface FieldViewerProps {
  name: string;
  children: (field: FieldHandler) => React.ReactElement | null;
}

export const FieldViewer: React.FC<FieldViewerProps> = ({ name, children }) => {
  const formService = useFormService();
  if (!formService) return null;
  const field = useField(name, formService);
  if (!field || typeof children !== 'function') return null;
  return children(field);
};

export const FormInput: React.FC<FormInputProps> = ({
  name,
  component,
  container,
  children,
  propMap = {},
}) => {
  const { defaultPropMap } = useContext(FormContext);

  const propMapWithFieldName = useMemo(() => {
    const finalPropMap = {};
    const mergedPropMap = { ...propMap, ...defaultPropMap };
    Object.keys(mergedPropMap).forEach(
      (key) => (finalPropMap[key] = name + '.' + mergedPropMap[key])
    );
    return finalPropMap;
  }, [propMap, defaultPropMap, name]);

  return useMemo(
    () => (
      <FormComponent
        component={component}
        propMap={propMapWithFieldName}
        container={container}
        children={children}
      />
    ),
    [propMapWithFieldName, component, container]
  );
};

interface GetFormComponentsPropsFn {
  (args: { propMap: PropMap; fields: (FieldHandler | undefined)[] });
}

const getFormComponentProps: GetFormComponentsPropsFn = ({
  propMap,
  fields,
}) => {
  let props = {};

  for (const field of fields) {
    if (!field) continue;

    for (const propPath of Object.keys(propMap)) {
      const fieldNameWithPath = propMap[propPath];
      const fieldAttributePath = fieldNameWithPath
        .split('.')
        .slice(1)
        .join('.');

      const fieldAttributeName = fieldAttributePath.split('.')[0];

      if (!field.hasOwnProperty(fieldAttributeName)) continue;

      const fieldValue = get(field, fieldAttributePath);
      if (propPath === '.') props = { ...props, ...fieldValue };
      else set(props, propPath, fieldValue);
    }
  }
  return props;
};

export const FormComponent: React.FC<FormComponentProps> = ({
  component,
  container,
  propMap,
}) => {
  const formService = useFormService();
  if (!formService) return null;
  const fieldDependencies = useMemo(() => {
    const fieldNames: string[] = [];
    for (const propPath in propMap) {
      const fieldAttributePath = propMap[propPath];
      const fieldName = fieldAttributePath.split('.')[0];
      if (fieldNames.indexOf(fieldName) === -1) fieldNames.push(fieldName);
    }
    return fieldNames;
  }, [propMap]);
  const fields = useFields(fieldDependencies, formService);
  const mappedProps = useMemo(() => {
    return getFormComponentProps({ propMap, fields });
  }, [propMap, fields]);
  const componentWithFormProps = useMemo(() => {
    const mergedProps = merge({ ...component.props }, mappedProps);
    // TODO: move this logic out, as this component shouldn't have to know about ComponentRenderer
    if (typeof component.type === 'string') {
      component = (
        <ComponentRenderer
          serializedComponent={
            {
              ...component,
              props: { ...component.props, ...mergedProps },
            } as ComponentDescriptor
          }
        />
      );

      return mergedProps.active !== false ? component : null;
    }

    return mergedProps.active !== false
      ? React.cloneElement(component, mergedProps)
      : null;
  }, [component, mappedProps]);

  return useMemo(() => {
    return container
      ? React.cloneElement(container, container.props, componentWithFormProps)
      : componentWithFormProps;
  }, [container, componentWithFormProps]);
};

export type SubmitProps<T extends React.ReactElement = React.ReactElement> = {
  component?: T;
  isLast?: boolean;
  wizardService?: any;
  disableOnField?: string;
} & {
  [K in keyof T['props']]: T['props'][K];
};

export const FormSubmit: React.FC<SubmitProps> = ({
  component = <Button />,
  isLast = false,
  ...props
}) => {
  const wizardStepIndex = useCurrentStepIndex();

  const formService = useFormService();
  const { state } = useForm(wizardStepIndex ?? '');

  if (!formService) return null;

  const { send } = formService;
  const overriddenProps = {
    ...component.props,
    ...props,
  };
  const onClick = (e) => {
    if (overriddenProps.onClick) overriddenProps.onClick(e);
    send('SUBMIT');
    if (state?.value === 'completed') {
      scrollToTop();
    }
  };
  return React.cloneElement(component, {
    ...overriddenProps,
    onClick,
  });
};

const contextDefaultValues = {
  name: '',
  validators: {},
  transformers: {},
};

const getTransitionListener =
  (
    formInterpreter: FormInterpreter,
    {
      onSubmit,
      onSubmitted,
      onBlur,
      onChange,
    }: Required<
      Pick<FormProps, 'onBlur' | 'onChange' | 'onSubmit' | 'onSubmitted'>
    >
  ) =>
  (s: FormState, e: FormEvent) => {
    if (e.type === 'SUBMIT') {
      const shouldSubmit = onSubmit(s, e, formInterpreter);
      if (shouldSubmit !== false) formInterpreter.execute(s);
    } else if (e.type === 'done.invoke.formValidation') {
      const shouldExecute = onSubmitted(s, e, formInterpreter);
      if (shouldExecute !== false) formInterpreter.execute(s);
    } else if (e.type === 'blur') {
      const shouldExecute = onBlur(s, e, formInterpreter);
      if (shouldExecute !== false) formInterpreter.execute(s);
    } else if (e.type.toLowerCase() === 'change') {
      const shouldExecute = onChange(s, e, formInterpreter);
      if (shouldExecute !== false) formInterpreter.execute(s);
    } else {
      formInterpreter.execute(s);
    }
  };

export const DEFAULT_PROP_MAP = {
  _label: 'label',
  subLabel: 'subLabel',
  _error: 'errors',
  _options: 'options',
  value: 'value',
  onChange: 'onChange',
  onBlur: 'onBlur',
  name: 'name',
  active: 'active',
};

export const Form = ({
  children = [],
  onSubmit = () => true,
  onSubmitted = () => true,
  onChange = () => true,
  onBlur = () => true,
  keepAlive = true,
  defaultPropMap = DEFAULT_PROP_MAP,
  interpreterCallback,
  onRender,
  formIndex,
  // formInterpreter,
  ...formContext
}: React.PropsWithChildren<FormProps>) => {
  const formInterpreter = useFormInterpreter(formContext.name ?? '');
  const interpreter =
    formInterpreter ??
    useInterpret<FormMachineContext, FormEvent>(
      formMachine.withContext({
        ...contextDefaultValues,
        ...formContext,
        keepAlive,
      }),
      {
        execute: false,
      }
    );

  useEffect(() => {
    if (typeof onRender === 'function' && formIndex !== undefined) {
      onRender(formIndex);
    }

    if (!formInterpreter) {
      const transitionListener = getTransitionListener(interpreter, {
        onChange,
        onSubmit,
        onSubmitted,
        onBlur,
      });
      interpreter.onTransition(transitionListener);
      return () => {
        interpreter.stop();
      };
    }
    return () => undefined;
  }, []);

  useEffect(() => {
    if (interpreterCallback) interpreterCallback(interpreter);
  }, [interpreterCallback, interpreter]);

  return (
    <FormContext.Provider
      value={{
        formService: formInterpreter ?? interpreter,
        defaultPropMap,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};
